import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, RotateCcw, Send } from 'lucide-react';
import {
  ChatMessage,
  useConversation,
  useMarkConversationRead,
  useSendReply,
  useSetConversationStatus,
} from '../../queries/supportChat';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';

const MAX_REPLY = 2000;
const DATETIME = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const fmt = (v: string) => DATETIME.format(new Date(v));

/** support writes; super-admin bypasses; operations and analysts read (admin-api customer-messages). */
export const canReply = (role: string) => role === 'super-admin' || role === 'support';

/** Who a message is from, as the thread labels it. */
export function senderLabel(message: ChatMessage): string {
  if (message.sender === 'tippa') return 'Tippa · automatic answer';
  if (message.sender === 'staff') return `Team · ${message.staffName ?? message.staffEmail ?? 'admin'}`;
  return message.askTippa ? 'Member · Ask Tippa' : 'Member';
}

/**
 * Under the team's latest reply: whether the member has read the chat since it arrived (their last read: opening the
 * chat with replies waiting, or writing), so support knows it has been seen. Null for every other message.
 */
export function seenLabel(message: ChatMessage, latestReplyId: number | undefined, userLastReadAt: string | null): string | null {
  if (message.sender !== 'staff' || message.id !== latestReplyId) return null;
  if (userLastReadAt && new Date(userLastReadAt) >= new Date(message.createdAt)) return `Seen ${fmt(userLastReadAt)}`;
  return 'Not seen yet';
}

function Bubble({ message, seen }: { message: ChatMessage; seen?: string | null }) {
  const fromTeam = message.sender !== 'user';
  const automatic = message.sender === 'tippa';
  return (
    <li className={cn('flex flex-col gap-1', fromTeam ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm',
          automatic
            ? 'rounded-br-sm border border-dashed border-primary/50 bg-primary/10 text-foreground'
            : fromTeam ? 'rounded-br-sm bg-primary text-primary-foreground' : 'rounded-bl-sm bg-muted text-foreground',
        )}
      >
        {message.body}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>{senderLabel(message)}</span>
        <span>{fmt(message.createdAt)}</span>
        {message.pagePath && (
          <code className="rounded bg-muted px-1.5 py-0.5 text-[10px]" title="Page the member was on">
            {message.pagePath}
          </code>
        )}
        {seen && <span className={cn('font-medium', seen.startsWith('Seen') ? 'text-success' : undefined)}>{seen}</span>}
      </div>
    </li>
  );
}

interface ConversationThreadProps {
  conversationId: number;
  /** Hide the member card (the user page already shows who it is). */
  compact?: boolean;
}

export function ConversationThread({ conversationId, compact = false }: ConversationThreadProps) {
  const { role } = useBrand();
  const { data, isLoading, error } = useConversation(conversationId);
  const send = useSendReply();
  const setStatus = useSetConversationStatus();
  const markRead = useMarkConversationRead();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLLIElement>(null);
  const writable = canReply(role);

  const messages = data?.messages ?? [];
  const latest = messages[messages.length - 1];
  const latestReplyId = [...messages].reverse().find((m) => m.sender === 'staff')?.id;

  // Opening the thread, or a new member message arriving while it is open, marks it read.
  // Only for a role that can answer: unread is support's to-do list, so browsing must not clear it.
  const markReadMutate = markRead.mutate;
  useEffect(() => {
    if (writable && latest?.sender === 'user') markReadMutate(conversationId);
  }, [writable, conversationId, latest?.id, latest?.sender, markReadMutate]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [latest?.id]);

  if (isLoading) return <Spinner label="Loading conversation…" />;
  if (error || !data) return <p className="p-6 text-sm text-destructive">Couldn't load this conversation.</p>;

  const { conversation } = data;
  const trimmed = draft.trim();
  const canSend = writable && trimmed.length > 0 && trimmed.length <= MAX_REPLY && !send.isPending;

  const submit = () => {
    if (!canSend) return;
    send.mutate(
      { id: conversationId, body: trimmed },
      { onSuccess: () => setDraft(''), onError: () => toast.error("Couldn't send the reply.") },
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const toggleStatus = () => {
    const next = conversation.status === 'open' ? 'resolved' : 'open';
    setStatus.mutate(
      { id: conversationId, status: next },
      { onError: () => toast.error("Couldn't update the conversation.") },
    );
  };

  return (
    <div className="flex h-full min-h-[420px] flex-col">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
        {compact ? (
          <div className="text-sm text-muted-foreground">Started {fmt(conversation.createdAt)}</div>
        ) : (
          <div className="min-w-0">
            <Link to={`/users/${conversation.userId}`} className="font-semibold hover:underline">
              {conversation.memberName ?? conversation.memberEmail ?? `User ${conversation.userId}`}
            </Link>
            <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
              {conversation.memberEmail && <span>{conversation.memberEmail}</span>}
              <span>{conversation.subscriptionLevel ?? 'no plan'}</span>
              <span>Member since {DATETIME.format(new Date(conversation.memberSince)).split(',')[0]}</span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!conversation.chatEnabled && <Badge variant="warning">Chat off for this member</Badge>}
          {conversation.status === 'open' ? <Badge variant="primary">Open</Badge> : <Badge variant="outline">Resolved</Badge>}
          {writable && (
            <Button variant="outline" size="sm" onClick={toggleStatus} disabled={setStatus.isPending}>
              {conversation.status === 'open' ? (
                <><CheckCircle2 className="h-4 w-4" /> Resolve</>
              ) : (
                <><RotateCcw className="h-4 w-4" /> Reopen</>
              )}
            </Button>
          )}
        </div>
      </header>

      <ol className="flex-1 space-y-4 overflow-y-auto p-4" aria-label="Messages">
        {messages.length === 0 && (
          <li className="py-8 text-center text-sm text-muted-foreground">No messages yet. Write the first one below.</li>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} message={m} seen={seenLabel(m, latestReplyId, data.conversation.userLastReadAt)} />
        ))}
        <li ref={endRef} aria-hidden="true" />
      </ol>

      {writable ? (
        <footer className="border-t border-border p-3">
          <label htmlFor={`reply-${conversationId}`} className="sr-only">Reply</label>
          <textarea
            id={`reply-${conversationId}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            maxLength={MAX_REPLY}
            placeholder="Write a reply… (Enter to send, Shift+Enter for a new line)"
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>Members see replies from the brand team, not your name. They're emailed if a reply sits unread for 30 minutes.</span>
            <Button size="sm" onClick={submit} disabled={!canSend}>
              <Send className="h-4 w-4" /> Send
            </Button>
          </div>
        </footer>
      ) : (
        <footer className="border-t border-border p-3 text-xs text-muted-foreground">Read only: replying needs the support role.</footer>
      )}
    </div>
  );
}
